import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import type {
  CreditPack,
  ModelPrice,
  RateCardEntry,
  RateCardResponse,
  UpsertCreditPackRequest,
  UpsertModelPriceRequest,
} from "@/types/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateTime } from "@/lib/utils";

/** Column order for the rate-card matrix; unknown action types append after. */
const ACTION_TYPE_ORDER = [
  "chat_message",
  "flow_step",
  "prompt_generation",
  "conversation_summary",
  "eval",
];

function sortActionTypes(types: string[]): string[] {
  return [...types].sort((a, b) => {
    const ia = ACTION_TYPE_ORDER.indexOf(a);
    const ib = ACTION_TYPE_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
}

export function BillingSettingsPage() {
  const { t } = useTranslation("billing");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("settings.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <RateCardSection />
      <ModelPricesSection />
      <CreditPacksSection />
    </div>
  );
}

// ── Rate card ───────────────────────────────────────────────────────────────

function RateCardSection() {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "rate-card"],
    queryFn: () => api.get<RateCardResponse>("/api/admin/billing/rate-card"),
  });

  // Matrix state: "employeeType|actionType" -> credits as string.
  const [matrix, setMatrix] = React.useState<Record<string, string>>({});
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (data && !initialized) {
      const next: Record<string, string> = {};
      for (const e of data.rateCard?.entries ?? []) {
        next[`${e.employeeType}|${e.actionType}`] = String(e.credits);
      }
      setMatrix(next);
      setInitialized(true);
    }
  }, [data, initialized]);

  const publishMutation = useMutation({
    mutationFn: (entries: RateCardEntry[]) =>
      api.put<{ rateCard: RateCardResponse["rateCard"] }>("/api/admin/billing/rate-card", {
        entries,
      }),
    onSuccess: (res) => {
      toast.success(t("settings.rateCard.published", { version: res.rateCard?.version }));
      setInitialized(false);
      qc.invalidateQueries({ queryKey: ["billing", "rate-card"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("settings.rateCard.publishFailed"));
    },
  });

  const employeeTypes = Array.from(
    new Set((data?.rateCard?.entries ?? []).map((e) => e.employeeType))
  ).sort();
  const actionTypes = sortActionTypes(
    Array.from(new Set((data?.rateCard?.entries ?? []).map((e) => e.actionType)))
  );

  function handlePublish() {
    const entries: RateCardEntry[] = [];
    for (const [key, raw] of Object.entries(matrix)) {
      const [employeeType, actionType] = key.split("|");
      const credits = Number(raw);
      if (!Number.isInteger(credits) || credits < 0) {
        toast.error(t("settings.rateCard.invalidCredits", { employeeType, actionType }));
        return;
      }
      entries.push({ employeeType, actionType, credits });
    }
    if (entries.length === 0) {
      toast.error(t("settings.rateCard.noEntries"));
      return;
    }
    if (
      !window.confirm(t("settings.rateCard.confirmPublish", { count: entries.length }))
    ) {
      return;
    }
    publishMutation.mutate(entries);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.rateCard.title")}</CardTitle>
        <CardDescription>
          {t("settings.rateCard.description")}
          {data?.rateCard
            ? t("settings.rateCard.current", {
                version: data.rateCard.version,
                date: formatDateTime(data.rateCard.effectiveFrom),
              })
            : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading || !initialized ? (
          <Skeleton className="h-48 w-full" />
        ) : error ? (
          <p className="text-sm text-destructive">
            {t("loadFailed", { message: error instanceof Error ? error.message : String(error) })}
          </p>
        ) : !data?.rateCard ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("settings.rateCard.empty")}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("settings.rateCard.employeeType")}</TableHead>
                    {actionTypes.map((at) => (
                      <TableHead key={at} className="font-mono text-xs">
                        {at}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {employeeTypes.map((et) => (
                    <TableRow key={et}>
                      <TableCell className="font-mono text-xs font-medium">{et}</TableCell>
                      {actionTypes.map((at) => {
                        const key = `${et}|${at}`;
                        return (
                          <TableCell key={key} className="min-w-[80px]">
                            <Input
                              aria-label={t("settings.rateCard.creditsAria", { employeeType: et, actionType: at })}
                              inputMode="numeric"
                              className="h-8 w-20"
                              value={matrix[key] ?? ""}
                              onChange={(e) =>
                                setMatrix((m) => ({ ...m, [key]: e.target.value }))
                              }
                            />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                {t("settings.rateCard.versionsTotal", { count: data.versions.length })}
                {data.versions.length > 1
                  ? t("settings.rateCard.previous", {
                      list: data.versions
                        .filter((v) => v.version !== data.rateCard?.version)
                        .slice(0, 3)
                        .map((v) => `v${v.version} (${t(`settings.rateCard.status.${v.status}`)})`)
                        .join(", "),
                    })
                  : ""}
              </p>
              <Button onClick={handlePublish} disabled={publishMutation.isPending}>
                {publishMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("settings.rateCard.publish")}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Model prices ────────────────────────────────────────────────────────────

interface ModelPriceRow {
  provider: string;
  model: string;
  input: string;
  output: string;
  cached: string;
  effectiveFrom: string;
  isNew: boolean;
}

function priceToRow(p: ModelPrice): ModelPriceRow {
  return {
    provider: p.provider,
    model: p.model,
    input: String(p.inputEurPer1m),
    output: String(p.outputEurPer1m),
    cached: p.cachedInputEurPer1m === null ? "" : String(p.cachedInputEurPer1m),
    effectiveFrom: p.effectiveFrom,
    isNew: false,
  };
}

function ModelPricesSection() {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "model-prices"],
    queryFn: () => api.get<{ prices: ModelPrice[] }>("/api/admin/billing/model-prices"),
  });

  const [rows, setRows] = React.useState<ModelPriceRow[]>([]);
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (data && !initialized) {
      setRows(data.prices.map(priceToRow));
      setInitialized(true);
    }
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: (prices: UpsertModelPriceRequest[]) =>
      api.put<{ prices: ModelPrice[] }>("/api/admin/billing/model-prices", { prices }),
    onSuccess: () => {
      toast.success(t("settings.modelPrices.saved"));
      setInitialized(false);
      qc.invalidateQueries({ queryKey: ["billing", "model-prices"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("settings.modelPrices.saveFailed"));
    },
  });

  function updateRow(index: number, patch: Partial<ModelPriceRow>) {
    setRows((rs) => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((rs) => [
      ...rs,
      {
        provider: "",
        model: "",
        input: "",
        output: "",
        cached: "",
        effectiveFrom: new Date().toISOString(),
        isNew: true,
      },
    ]);
  }

  function handleSave() {
    const prices: UpsertModelPriceRequest[] = [];
    for (const row of rows) {
      if (!row.provider.trim() || !row.model.trim()) {
        toast.error(t("settings.modelPrices.missingProviderModel"));
        return;
      }
      const input = Number(row.input);
      const output = Number(row.output);
      if (!Number.isFinite(input) || input < 0 || !Number.isFinite(output) || output < 0) {
        toast.error(t("settings.modelPrices.invalidPrices", { provider: row.provider, model: row.model }));
        return;
      }
      const cached = row.cached.trim() === "" ? null : Number(row.cached);
      if (cached !== null && (!Number.isFinite(cached) || cached < 0)) {
        toast.error(t("settings.modelPrices.invalidCached", { provider: row.provider, model: row.model }));
        return;
      }
      prices.push({
        provider: row.provider.trim(),
        model: row.model.trim(),
        inputEurPer1m: input,
        outputEurPer1m: output,
        cachedInputEurPer1m: cached,
        effectiveFrom: row.effectiveFrom,
      });
    }
    if (prices.length === 0) {
      toast.error(t("nothingToSave"));
      return;
    }
    saveMutation.mutate(prices);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.modelPrices.title")}</CardTitle>
        <CardDescription>{t("settings.modelPrices.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading || !initialized ? (
          <Skeleton className="h-40 w-full" />
        ) : error ? (
          <p className="text-sm text-destructive">
            {t("loadFailed", { message: error instanceof Error ? error.message : String(error) })}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("settings.modelPrices.provider")}</TableHead>
                    <TableHead>{t("settings.modelPrices.model")}</TableHead>
                    <TableHead>{t("settings.modelPrices.input")}</TableHead>
                    <TableHead>{t("settings.modelPrices.output")}</TableHead>
                    <TableHead>{t("settings.modelPrices.cached")}</TableHead>
                    <TableHead>{t("settings.modelPrices.effectiveFrom")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                        {t("settings.modelPrices.empty")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, i) => (
                      <TableRow key={`${row.provider}|${row.model}|${row.effectiveFrom}|${i}`}>
                        <TableCell>
                          {row.isNew ? (
                            <Input
                              aria-label={t("settings.modelPrices.provider")}
                              className="h-8 w-28"
                              placeholder="openai"
                              value={row.provider}
                              onChange={(e) => updateRow(i, { provider: e.target.value })}
                            />
                          ) : (
                            <span className="font-mono text-xs">{row.provider}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.isNew ? (
                            <Input
                              aria-label={t("settings.modelPrices.model")}
                              className="h-8 w-48"
                              placeholder="gpt-4o"
                              value={row.model}
                              onChange={(e) => updateRow(i, { model: e.target.value })}
                            />
                          ) : (
                            <span className="font-mono text-xs">{row.model}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t("settings.modelPrices.inputAria", { model: row.model || t("settings.modelPrices.newModel") })}
                            inputMode="decimal"
                            className="h-8 w-24"
                            value={row.input}
                            onChange={(e) => updateRow(i, { input: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t("settings.modelPrices.outputAria", { model: row.model || t("settings.modelPrices.newModel") })}
                            inputMode="decimal"
                            className="h-8 w-24"
                            value={row.output}
                            onChange={(e) => updateRow(i, { output: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t("settings.modelPrices.cachedAria", { model: row.model || t("settings.modelPrices.newModel") })}
                            inputMode="decimal"
                            className="h-8 w-24"
                            placeholder="—"
                            value={row.cached}
                            onChange={(e) => updateRow(i, { cached: e.target.value })}
                          />
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDateTime(row.effectiveFrom)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={addRow}>
                <Plus className="mr-1 h-4 w-4" /> {t("settings.modelPrices.add")}
              </Button>
              <Button onClick={handleSave} disabled={saveMutation.isPending}>
                {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("settings.modelPrices.save")}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Credit packs ────────────────────────────────────────────────────────────

interface PackRow {
  code: string;
  name: string;
  credits: string;
  amountMajor: string;
  currency: string;
  expiresInMonths: string;
  active: boolean;
  sort: string;
  isNew: boolean;
}

function packToRow(p: CreditPack): PackRow {
  return {
    code: p.code,
    name: p.name,
    credits: String(p.credits),
    amountMajor: (p.amountCents / 100).toFixed(2),
    currency: p.currency,
    expiresInMonths: p.expiresInMonths === null ? "" : String(p.expiresInMonths),
    active: p.active,
    sort: String(p.sort),
    isNew: false,
  };
}

function CreditPacksSection() {
  const { t } = useTranslation("billing");
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["billing", "credit-packs"],
    queryFn: () => api.get<{ packs: CreditPack[] }>("/api/admin/billing/credit-packs"),
  });

  const [rows, setRows] = React.useState<PackRow[]>([]);
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    if (data && !initialized) {
      setRows(data.packs.map(packToRow));
      setInitialized(true);
    }
  }, [data, initialized]);

  const saveMutation = useMutation({
    mutationFn: (packs: UpsertCreditPackRequest[]) =>
      api.put<{ packs: CreditPack[] }>("/api/admin/billing/credit-packs", { packs }),
    onSuccess: () => {
      toast.success(t("settings.creditPacks.saved"));
      setInitialized(false);
      qc.invalidateQueries({ queryKey: ["billing", "credit-packs"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("settings.creditPacks.saveFailed"));
    },
  });

  function updateRow(index: number, patch: Partial<PackRow>) {
    setRows((rs) => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRow() {
    setRows((rs) => [
      ...rs,
      {
        code: "",
        name: "",
        credits: "",
        amountMajor: "",
        currency: "EUR",
        expiresInMonths: "",
        active: true,
        sort: String((rs.length + 1) * 10),
        isNew: true,
      },
    ]);
  }

  function handleSave() {
    const packs: UpsertCreditPackRequest[] = [];
    for (const row of rows) {
      if (!row.code.trim() || !row.name.trim()) {
        toast.error(t("settings.creditPacks.missingCodeName"));
        return;
      }
      const credits = Number(row.credits);
      if (!Number.isInteger(credits) || credits <= 0) {
        toast.error(t("settings.creditPacks.invalidCredits", { code: row.code }));
        return;
      }
      const amount = Number(row.amountMajor);
      if (!Number.isFinite(amount) || amount < 0) {
        toast.error(t("settings.creditPacks.invalidPrice", { code: row.code }));
        return;
      }
      const expires = row.expiresInMonths.trim() === "" ? null : Number(row.expiresInMonths);
      if (expires !== null && (!Number.isInteger(expires) || expires <= 0)) {
        toast.error(t("settings.creditPacks.invalidExpiry", { code: row.code }));
        return;
      }
      const sort = Number(row.sort);
      packs.push({
        code: row.code.trim(),
        name: row.name.trim(),
        credits,
        amountCents: Math.round(amount * 100),
        currency: row.currency.trim().toUpperCase() || "EUR",
        expiresInMonths: expires,
        active: row.active,
        sort: Number.isInteger(sort) ? sort : 0,
      });
    }
    if (packs.length === 0) {
      toast.error(t("nothingToSave"));
      return;
    }
    saveMutation.mutate(packs);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.creditPacks.title")}</CardTitle>
        <CardDescription>{t("settings.creditPacks.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading || !initialized ? (
          <Skeleton className="h-32 w-full" />
        ) : error ? (
          <p className="text-sm text-destructive">
            {t("loadFailed", { message: error instanceof Error ? error.message : String(error) })}
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("settings.creditPacks.code")}</TableHead>
                    <TableHead>{t("settings.creditPacks.name")}</TableHead>
                    <TableHead>{t("settings.creditPacks.credits")}</TableHead>
                    <TableHead>{t("settings.creditPacks.price")}</TableHead>
                    <TableHead>{t("settings.creditPacks.expires")}</TableHead>
                    <TableHead>{t("settings.creditPacks.sort")}</TableHead>
                    <TableHead>{t("settings.creditPacks.active")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                        {t("settings.creditPacks.empty")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, i) => (
                      <TableRow key={row.isNew ? `new-${i}` : row.code}>
                        <TableCell>
                          {row.isNew ? (
                            <Input
                              aria-label={t("settings.creditPacks.codeAria")}
                              className="h-8 w-28"
                              placeholder="pack_xl"
                              value={row.code}
                              onChange={(e) => updateRow(i, { code: e.target.value })}
                            />
                          ) : (
                            <span className="font-mono text-xs">{row.code}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t("settings.creditPacks.nameAria", { code: row.code || t("settings.creditPacks.newPack") })}
                            className="h-8 w-56"
                            value={row.name}
                            onChange={(e) => updateRow(i, { name: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t("settings.creditPacks.creditsAria", { code: row.code || t("settings.creditPacks.newPack") })}
                            inputMode="numeric"
                            className="h-8 w-24"
                            value={row.credits}
                            onChange={(e) => updateRow(i, { credits: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Input
                              aria-label={t("settings.creditPacks.priceAria", { code: row.code || t("settings.creditPacks.newPack") })}
                              inputMode="decimal"
                              className="h-8 w-24"
                              value={row.amountMajor}
                              onChange={(e) => updateRow(i, { amountMajor: e.target.value })}
                            />
                            <span className="text-xs text-muted-foreground">{row.currency}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t("settings.creditPacks.expiresAria", { code: row.code || t("settings.creditPacks.newPack") })}
                            inputMode="numeric"
                            className="h-8 w-20"
                            placeholder="—"
                            value={row.expiresInMonths}
                            onChange={(e) => updateRow(i, { expiresInMonths: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            aria-label={t("settings.creditPacks.sortAria", { code: row.code || t("settings.creditPacks.newPack") })}
                            inputMode="numeric"
                            className="h-8 w-16"
                            value={row.sort}
                            onChange={(e) => updateRow(i, { sort: e.target.value })}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            aria-label={t("settings.creditPacks.activeAria", { code: row.code || t("settings.creditPacks.newPack") })}
                            checked={row.active}
                            onCheckedChange={(v) => updateRow(i, { active: v })}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={addRow}>
                <Plus className="mr-1 h-4 w-4" /> {t("settings.creditPacks.add")}
              </Button>
              <Button onClick={handleSave} disabled={saveMutation.isPending}>
                {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("settings.creditPacks.save")}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
